import type { Locale } from '@seek/melways-sites';
import { Box, TabPanel, TabPanels, TabsProvider } from 'braid-design-system';
import { observer } from 'mobx-react';
import type { FC, PropsWithChildren } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import PreviousApplications from 'src/App/AppPageBlock/PreviousApplications/PreviousApplications';
import { ProfileBody } from 'src/App/AppPageBlock/Profile/ProfileBody';
import type { mapToSimplifiedAnswers } from 'src/components/list/JobApplicationCard/RoleRequirementsView/RoleRequirement/mapToSimplifiedAnswers';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import type { IAttachment } from 'src/stores/JobApplicationStore/Attachment';
import {
  attachmentUrlV2,
  mapStoreAttachmentTypeToRequestInput,
} from 'src/utils/attachmentUrls';
import { convertToIsVerifiedId } from 'src/utils/eventMappers';
import {
  trackClickedNotesTab,
  trackClickedProfileTab,
  trackClickedEmailsTab,
  trackClickedResumeTab,
  trackClickedCoverLetterTab,
  trackClickedSelectionCriteriaTab,
  trackClickedPreviousApplicationsTab,
} from 'src/utils/events';
import { sendHotjarTag } from 'src/utils/hotjar';
import { sortStoreAttachments } from 'src/utils/sortStoreAttachments';

import { HOTJAR_TAG_PROFILE } from '../../../../constants/hotjar';
import { JOB_APPLICATION_TAB } from '../../../../constants/tabs';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from '../../../../pages/CandidateManagementPage/queryParams';
import type { IJobApplication } from '../../../../stores/JobApplicationStore/JobApplication';
import NotesAttachments from '../../NotesAttachments';
import PdfViewer from '../../PdfViewer';
import ProspectEmail from '../../ProspectEmail';
import type transformProfileToComponentProps from '../Profile/service/transformProfile';
import TabSelect, { type TabSelectOption } from '../TabSelect';

import { useGetDetailsTabs } from './hooks/useGetDetailsTabs';

interface Props {
  jobApplication: IJobApplication;
  isMobile: boolean;
  locale: Locale;
  shouldShowVerifiedCredentials?: boolean;
  jobId: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  transformedProfile: ReturnType<typeof transformProfileToComponentProps>;
  applicationQuestions: ReturnType<typeof mapToSimplifiedAnswers>;
}

export interface DetailTabSelectOption extends TabSelectOption {
  attachment?: IAttachment;
  counts?: number;
  value: JOB_APPLICATION_TAB;
}

const DetailsTabView = ({
  jobApplication,
  isMobile,
  locale,
  shouldShowVerifiedCredentials,
  jobId,
  addCustomError,
  transformedProfile,
  applicationQuestions,
}: Props) => {
  const {
    notes,
    attachmentsStore,
    prospectEmailStore,
    candidateStore,
    profile,
    verifications,
  } = jobApplication;

  const isVerifiedId = convertToIsVerifiedId(jobApplication.profile);

  const [, setTabQueryParam] = useQueryParam(
    CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
    StringParam,
  );
  const tabs = useGetDetailsTabs(jobApplication, isMobile);
  const getDocumentTrackingAttributes = (tab: JOB_APPLICATION_TAB) => {
    const doc = tabs.find((x) => x.value === tab)?.attachment;
    return {
      documentType: doc?.typeName,
      attachmentId: doc?.id,
      ...jobApplication.baseTrackingAttributes,
    };
  };

  const onTabChange = (_selectedIndex: number, tab?: string) => {
    jobApplication.selectTabInDetailView(tab as JOB_APPLICATION_TAB);
    switch (tab) {
      case JOB_APPLICATION_TAB.NOTES_ATTACHMENTS:
        trackClickedNotesTab(notes.trackingAttributes, {
          currentPage: TRACKING_CANDIDATE_DETAILS,
        });
        setTabQueryParam(JOB_APPLICATION_TAB.NOTES_ATTACHMENTS);
        return;
      case JOB_APPLICATION_TAB.PROFILE:
        trackClickedProfileTab(jobApplication.baseTrackingAttributes);
        sendHotjarTag(HOTJAR_TAG_PROFILE);
        setTabQueryParam(JOB_APPLICATION_TAB.PROFILE);
        return;
      case JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS:
        trackClickedPreviousApplicationsTab(
          jobApplication.baseTrackingAttributes,
        );
        setTabQueryParam(JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS);
        return;
      case JOB_APPLICATION_TAB.EMAILS:
        trackClickedEmailsTab(prospectEmailStore.trackingAttributes);
        setTabQueryParam(JOB_APPLICATION_TAB.EMAILS);
        return;
      case JOB_APPLICATION_TAB.RESUME:
        trackClickedResumeTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.RESUME);
        return;
      case JOB_APPLICATION_TAB.COVER_LETTER:
        trackClickedCoverLetterTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.COVER_LETTER);
        return;
      case JOB_APPLICATION_TAB.SELECTION_CRITERIA:
        trackClickedSelectionCriteriaTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.SELECTION_CRITERIA);
        return;
      default:
        throw new Error('Unknown tab name');
    }
  };

  const getTabPanel = (tab: DetailTabSelectOption) => {
    switch (tab.value) {
      case JOB_APPLICATION_TAB.PROFILE:
        return (
          <DetailsContentViewWrapper>
            <ProfileBody
              jobApplicationId={jobApplication.id}
              applicationDocuments={{
                attachments: sortStoreAttachments(
                  jobApplication.attachments,
                ).map((attachment) => {
                  const downloadUrl = attachmentUrlV2({
                    jobId,
                    applicationCorrelationId:
                      jobApplication.applicationCorrelationId,
                    attachmentType: mapStoreAttachmentTypeToRequestInput(
                      attachment.type,
                    ),
                    actionType: 'download',
                    blobId: attachment.id,
                  });

                  return {
                    id: attachment.id,
                    type: attachment.hirerGraphType,
                    filename: attachment.filename,
                    typeName: attachment.typeName,
                    downloadUrl,
                  };
                }),
                tracking: {
                  applicationCorrelationId:
                    jobApplication.applicationCorrelationId,
                  applicationId: jobApplication.jobApplicationId,
                  seekCandidateId: jobApplication.seekCandidateId,
                  adcentreCandidateId: jobApplication.candidateId,
                  prospectId: jobApplication.id,
                  isVerifiedId: profile?.verifiedIdentity?.fullName ? 1 : 0,
                },
              }}
              roles={transformedProfile?.roles ?? []}
              educationItems={
                transformedProfile?.qualifications.map((education) => ({
                  id: education.id,
                  title: education.name.text,
                  institute: education.institute.text,
                  highlights: education.highlights,
                  completed: education.completed,
                  completionDate: education.completionDate,
                  verificationViewFields: education.credential?.credentialInfo,
                })) ?? []
              }
              verifiedIdentity={
                profile?.verifiedIdentity?.fullName
                  ? {
                      fullName: profile.verifiedIdentity.fullName,
                    }
                  : null
              }
              licences={
                profile?.licences.map((licence) => ({
                  id: licence.id,
                  title: licence.name.text,
                  issuingOrganisation: licence.issuingOrganisation,
                  formattedDate: licence.formattedExpiryDate,
                  description: licence.description,
                  verificationViewFields: licence.credential?.credentialInfo,
                })) ?? []
              }
              employmentStatus={{
                nationalities: transformedProfile?.nationalities ?? [],
                rightsToWork:
                  transformedProfile?.rightsToWorkV2.map(
                    ({ displayLabel }) => displayLabel,
                  ) ?? [],
              }}
              applicationQuestions={{
                questions: applicationQuestions,
                allVerifications: verifications,
              }}
              skills={profile?.skills.map(({ keyword }) => keyword.text) ?? []}
              shouldShowVerifiedCredentials={shouldShowVerifiedCredentials}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.RESUME:
      case JOB_APPLICATION_TAB.COVER_LETTER:
      case JOB_APPLICATION_TAB.SELECTION_CRITERIA:
        const attachment = tab.attachment;
        if (attachment && attachment.jobApplication) {
          return (
            <DetailsContentViewWrapper>
              <PdfViewer
                attachment={attachment}
                isVerifiedId={isVerifiedId}
                jobId={jobId}
                applicationCorrelationId={
                  jobApplication.applicationCorrelationId
                }
                applicationId={jobApplication.jobApplicationId}
                seekCandidateId={jobApplication.seekCandidateId}
                adcentreCandidateId={jobApplication.candidateId}
                isManuallyAddedCandidate={jobApplication.source !== 1}
              />
            </DetailsContentViewWrapper>
          );
        }
        return <TabPanel> </TabPanel>;
      case JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS:
        return (
          <DetailsContentViewWrapper>
            <PreviousApplications
              jobId={jobId}
              candidateStore={candidateStore}
              isVerifiedId={isVerifiedId}
              addCustomError={addCustomError}
              locale={locale}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.EMAILS:
        return (
          <DetailsContentViewWrapper>
            <ProspectEmail
              prospectEmailStore={prospectEmailStore}
              locale={locale}
              addCustomError={addCustomError}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.NOTES_ATTACHMENTS:
        return (
          <DetailsContentViewWrapper>
            <NotesAttachments
              notes={notes}
              attachmentsStore={attachmentsStore}
              locale={locale}
              isVerifiedId={isVerifiedId}
              jobId={jobId}
              applicationCorrelationId={jobApplication.applicationCorrelationId}
              addCustomError={addCustomError}
            />
          </DetailsContentViewWrapper>
        );
      default:
        return <></>;
    }
  };

  return (
    <Box zIndex={0}>
      <TabsProvider
        id="tab-select-detail-view"
        selectedItem={jobApplication.selectedTabInDetailView}
        onChange={onTabChange}
      >
        <TabSelect testId="details-tab-selector" options={tabs} />
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          background="surface"
        >
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel item={tab.value} key={tab.value}>
                {getTabPanel(tab)}
              </TabPanel>
            ))}
          </TabPanels>
        </Box>
      </TabsProvider>
    </Box>
  );
};

const DetailsContentViewWrapper: FC<PropsWithChildren> = ({ children }) => (
  <Box paddingTop="large" paddingBottom="medium" background="surface">
    {children}
  </Box>
);

export default observer(DetailsTabView);
